// useApiFunctions.js
import { TLessonAnswerUser } from "../../models/courses/lesson";
import { TDashboard } from "../../models/user/Dashboard";
import {
  TPassword,
  TRole,
  TRoleUserType,
  TUserApp,
  TUserappLog,
  SaveUserDetailsResult,
} from "../../models/user/User";
import {
  TLessonProgress,
  TUserAnswer,


} from "../../models/user/UserProgression";
import { useApiClient } from "../apiClients";
import {
  ENDPOINTS_CODE_ANSWERS,
  ENDPOINTS_DASHBOARD,
  ENDPOINTS_PASSWORDS,
  ENDPOINTS_PROGRESS,


  ENDPOINTS_ROLES,
  ENDPOINTS_USER_ANSWERS,
  ENDPOINTS_USERAPP,
  ENDPOINTS_USERAPP_LOGS,

} from "./endpoints";

export const useUserApi = () => {
  const tilmicodeApiClient = useApiClient();
  const identityApiClient = useApiClient("identity");

  // -------------------------------------------------------------------------
  // UserApps
  // -------------------------------------------------------------------------
  const getAllUserApps = () =>
    identityApiClient.get<TUserApp[]>(ENDPOINTS_USERAPP.GET_ALL);
  const getUserAppById = (id: number) =>
    identityApiClient.get<TUserApp>(ENDPOINTS_USERAPP.GET_BY_ID(id));
  const createUserApp = (data: TUserApp) =>
    identityApiClient.post(ENDPOINTS_USERAPP.POST, data);
  const updateUserApp = (id: number, data: TUserApp) =>
    identityApiClient.put(ENDPOINTS_USERAPP.PUT(id), data);
  const activateUserApp = (id: number) =>
    identityApiClient.put<TUserApp>(ENDPOINTS_USERAPP.ACTIVATE(id));
  const deactivateUserApp = (id: number) =>
    identityApiClient.put<TUserApp>(ENDPOINTS_USERAPP.DEACTIVATE(id));
  const deleteUserApp = (id: number) =>
    identityApiClient.delete(ENDPOINTS_USERAPP.DELETE(id));
  const generateLoginUserApp = (data: { firstname: string; lastname: string }) =>
    identityApiClient.get(ENDPOINTS_USERAPP.GENERATE_LOGIN, {
      params: data,
    });

  const getOrganizationByUserappId = (id: number) =>
    identityApiClient.get<TUserApp>(ENDPOINTS_USERAPP.GET_ORGANIZATION_BY_USER_ID(id));

  const getAllUsersAffected = () =>
    identityApiClient.get<TUserApp[]>(ENDPOINTS_USERAPP.GET_ALL_AFFECTED_DETAILS);
  const getUserDetails = (id: number) =>
    identityApiClient.get<TUserApp>(ENDPOINTS_USERAPP.GET_USER_DETAILS(id));
  const getAllUsersForSearch = (
    typeRelationId: number,
    typeUserParentId: number
  ) =>
    identityApiClient.get<TUserApp[]>(
      ENDPOINTS_USERAPP.GET_ALL_BY_TYPE(typeRelationId, typeUserParentId)
    );
  const saveUserWithDetails = (data: TUserApp) =>
    identityApiClient.post<SaveUserDetailsResult>(ENDPOINTS_USERAPP.POST_SAVE_DETAILS, data);

  // Metadata operations
  const updateUserMetadata = (id: number, metadata: Record<string, any>) =>
    identityApiClient.put(ENDPOINTS_USERAPP.PUT_METADATA(id), { metadata });
  const getUserMetadata = (id: number) =>
    identityApiClient.get<{ metadata: any }>(ENDPOINTS_USERAPP.GET_METADATA(id));

  // -------------------------------------------------------------------------
  // Passwords
  // -------------------------------------------------------------------------
  const getPasswordsByUserAppId = (id: number) =>
    identityApiClient.get<TPassword[]>(ENDPOINTS_PASSWORDS.GET_BY_IDUSERAPP(id));
  const createPassword = (data: TPassword) =>
    identityApiClient.post(ENDPOINTS_PASSWORDS.POST, data);
  const updatePassword = (data: TPassword) =>
    identityApiClient.put(ENDPOINTS_PASSWORDS.PUT, data);
  const deletePassword = (id: number) =>
    identityApiClient.delete(ENDPOINTS_PASSWORDS.DELETE(id));

  // -------------------------------------------------------------------------
  // Progression
  // -------------------------------------------------------------------------
  const getProgressionUserByCourseId = (course_id: number) =>
    tilmicodeApiClient.get<TLessonProgress[]>(
      ENDPOINTS_PROGRESS.GET_BY_IDCOURSE(course_id)
    );
  const saveProgressionUser = (lesson_id: number) =>
    tilmicodeApiClient.post(ENDPOINTS_PROGRESS.POST_SAVE(lesson_id));
  const getProgressStateByCourseId = (course_id: number) =>
    tilmicodeApiClient.get<TLessonProgress[]>(
      ENDPOINTS_PROGRESS.GET_PROGRESS_BY_IDCOURSE(course_id)
    );
  const getProgressStateBySubjectId = (subject_id: number) =>
    tilmicodeApiClient.get<TLessonProgress[]>(
      ENDPOINTS_PROGRESS.GET_PROGRESS_BY_IDSUBJECT(subject_id)
    );

  // -------------------------------------------------------------------------
  // Question answers
  // -------------------------------------------------------------------------
  const getQuestionAnswersByQuestionId = (question_id: number) =>
    tilmicodeApiClient.get<TLessonAnswerUser[]>(
      ENDPOINTS_USER_ANSWERS.GET_BY_QUESTION_ID(question_id)
    );
  const saveQuestionAnswers = (data: TLessonAnswerUser[]) =>
    tilmicodeApiClient.post<TLessonAnswerUser[]>(ENDPOINTS_USER_ANSWERS.POST_SAVE, data);

  // -------------------------------------------------------------------------
  // Roles
  // -------------------------------------------------------------------------
  const getAllRoles = () => identityApiClient.get<TRole[]>(ENDPOINTS_ROLES.GET_ALL);
  const getAllRolesTypeUsersLinks = () =>
    identityApiClient.get<TRoleUserType[]>(ENDPOINTS_ROLES.GEL_ALL_LINKED_ROLES);
  const createRoleTypeUserLink = (data: TRoleUserType) =>
    identityApiClient.post(ENDPOINTS_ROLES.POST_ROLE_TO_TYPE_USER, data);
  const deleteRoleTypeUserLink = (id: number) =>
    identityApiClient.delete(ENDPOINTS_ROLES.DELETE_ROLE_TO_TYPE_USER(id));

  // -------------------------------------------------------------------------
  // Dashboard
  // -------------------------------------------------------------------------
  const getDashboard = () =>
    tilmicodeApiClient.get<TDashboard>(ENDPOINTS_DASHBOARD.GET_DASHBOARD);

  // -------------------------------------------------------------------------
  // User Answers (centralized for all exercise types)
  // -------------------------------------------------------------------------
  const getUserAnswer = () =>
    tilmicodeApiClient.get<TUserAnswer[]>(ENDPOINTS_CODE_ANSWERS.GET_BY_USER);

  const getUserAnswerById = (lesson_id: number) =>
    tilmicodeApiClient.get<TUserAnswer>(
      ENDPOINTS_CODE_ANSWERS.GET_BY_LESSON(lesson_id)
    );

  const saveUserAnswerById = (
    lesson_id: number,
    data: Partial<TUserAnswer>
  ) =>
    tilmicodeApiClient.post<TUserAnswer>(
      ENDPOINTS_CODE_ANSWERS.POST_SAVE(lesson_id),
      data
    );

  // -------------------------------------------------------------------------
  // Userapp logs
  // -------------------------------------------------------------------------
  const getUserLogsByUserId = (id: number) =>
    identityApiClient.get<TUserappLog[]>(ENDPOINTS_USERAPP_LOGS.GET_BY_USER(id));


  return {
    // ---------------------------------------------------------------------
    // UserApps
    getAllUserApps,
    getUserAppById,
    createUserApp,
    updateUserApp,
    activateUserApp,
    deactivateUserApp,
    deleteUserApp,
    generateLoginUserApp,
    getOrganizationByUserappId,

    getAllUsersAffected,
    getUserDetails,
    getAllUsersForSearch,
    saveUserWithDetails,
    updateUserMetadata,
    getUserMetadata,

    // ---------------------------------------------------------------------
    // Passwords
    createPassword,
    updatePassword,
    deletePassword,
    getPasswordsByUserAppId,

    // ---------------------------------------------------------------------
    // Progress
    getProgressionUserByCourseId,
    saveProgressionUser,
    getProgressStateByCourseId,
    getProgressStateBySubjectId,

    // ---------------------------------------------------------------------
    // Question answers
    getQuestionAnswersByQuestionId,
    saveQuestionAnswers,

    // ---------------------------------------------------------------------
    // Roles
    getAllRoles,
    getAllRolesTypeUsersLinks,
    createRoleTypeUserLink,
    deleteRoleTypeUserLink,

    // ---------------------------------------------------------------------
    // Dashboard
    getDashboard,

   // ---------------------------------------------------------------------
    // User answers
    getUserAnswer,
    getUserAnswerById,
    saveUserAnswerById,

    // ---------------------------------------------------------------------
    // Userapp logs
    getUserLogsByUserId,

  };
};


